import React, { useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import type { DateRangeFormatFunction, stringOrDate } from 'react-big-calendar';
import { Calendar as ReactBigCalendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import type { Moment } from 'moment';
import moment from 'moment';
import Color from 'color';
import Event, { convertBlockedSlotToArgs } from '@this/domain/appoint/event';
import { travelTimeTitle } from '@this/domain/appoint/candidate';
import type Calendar from '@this/domain/appoint/calendar';
import type SlotInfo from '@this/domain/appoint/slot_info';
import type OpenMeeting from '@this/domain/appoint/open_meeting';
import OpenMeetingBlockedSlot, {
  blockedSlotMobileTitle,
  blockedSlotDisabledTitle
} from '@this/domain/appoint/open_meeting_blocked_slot';
import { CalendarWrapper, CalendarLoading, formats, messages } from '@this/components/appoint/common';
import AppointContext from '@this/components/appoint/appoint_context';
import EventPopupCard from '@this/components/appoint/shared/event_popup_card/event_popup_card';
import muiTheme from '@this/components/appoint/mui_theme';

const DragAndDropCalendar = withDragAndDrop(ReactBigCalendar);

type EventInfo = { event: Event; start: stringOrDate; end: stringOrDate; allDay: boolean };

const localizer = momentLocalizer(moment);

interface Props {
  openMeeting: OpenMeeting;
  blockedSlots: OpenMeetingBlockedSlot[];
  eventLists: Event[][];
  calendars: Calendar[];
  date: Moment;
  isEventLoading: boolean;
  showBlockedSlotsForm: boolean;
  onDateChange: (date: Moment) => void;
  openMeetingBlockedSlotsChange: (blockedSlots: OpenMeetingBlockedSlot[]) => void;
}

const BigCalendar = ({
  openMeeting,
  blockedSlots,
  eventLists,
  calendars,
  date,
  isEventLoading,
  showBlockedSlotsForm,
  onDateChange,
  openMeetingBlockedSlotsChange
}: Props) => {
  const contextValue = useContext(AppointContext);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventClickX, setEventClickX] = useState<number>(0);
  const [eventClickY, setEventClickY] = useState<number>(0);
  const [calendarScrollTo, setCalendarScrollTo] = useState<Date | undefined>(
    moment().set({ hour: 5, minute: 50 }).toDate()
  );
  const [selectedCalendarView, setSelectedCalendarView] = useState<'week' | 'day'>('week');
  const [touchStartedX, setTouchStartedX] = useState<number>(0);
  const [touchMovedX, setTouchMovedX] = useState<number>(0);
  const [isLongTapInfoShown, setIsLongTapInfoShown] = useState<boolean>(false);

  if (contextValue.isMobile) {
    const dayRangeHeaderFormat: DateRangeFormatFunction = ({ start }) => {
      const s = moment(start);
      return s.format('YYYY年MM月DD日(ddd) 週');
    };
    formats.dayRangeHeaderFormat = dayRangeHeaderFormat;
  }

  const updateEvents = () => {
    setEvents([
      ...(eventLists.length > 0 ? eventLists.flat() : []),
      ...blockedSlots
        .map(s => {
          let name;
          if (!showBlockedSlotsForm) {
            name = blockedSlotDisabledTitle;
          } else if (contextValue.isMobile) {
            name = blockedSlotMobileTitle;
          }

          return new Event(convertBlockedSlotToArgs(s, name));
        })
        .flat()
    ]);
  };

  useEffect(() => {
    updateEvents();
  }, [showBlockedSlotsForm, eventLists, blockedSlots]);

  const showPastError = () => {
    contextValue.setSnackbarContent({ message: '過去日時は選択できません', severity: 'error' });
    contextValue.setOpenSnackbar(true);
  };

  // 既存の除外日時と重複があればマージする
  const mergeBlockedSlot = (start: Moment, end: Moment) => {
    for (let i = 0; i < blockedSlots.length; i += 1) {
      const s = blockedSlots[i];
      if (
        (s.startAt.isSame(end) || s.startAt.isBefore(end)) &&
        (s.endAt.isSame(start) || s.endAt.isAfter(start))
      ) {
        s.startAt = moment.min([s.startAt, start]);
        s.endAt = moment.max([s.endAt, end]);
        openMeetingBlockedSlotsChange(blockedSlots.sort((a, b) => a.startAt.diff(b.startAt)));
        updateEvents();
        return true;
      }
    }
    return false;
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    setSelectedEvent(null);
    const start = moment(slot.start);
    const end = moment(slot.end);

    const tappedBlockedSlot = _.find(
      blockedSlots,
      s => s.startAt.format() <= start.format() && s.endAt.format() >= end.format()
    );
    if (tappedBlockedSlot) {
      openMeeting.removeBlockedSlot(tappedBlockedSlot);
      updateEvents();
      return;
    }

    if (start.isBefore(moment())) {
      showPastError();
      return;
    }

    if (showBlockedSlotsForm) {
      if (mergeBlockedSlot(start, end)) {
        return;
      }

      // マージできる既存除外日時がなければ作成
      const blockedSlot = new OpenMeetingBlockedSlot({ startAt: start, endAt: end });
      openMeetingBlockedSlotsChange([...blockedSlots, blockedSlot].sort((a, b) => a.startAt.diff(b.startAt)));
      updateEvents();
    }
  };

  const handleDoubleClickEvent = (event: Event) => {
    if (contextValue.isMobile || !event.isBlockedSlot || !showBlockedSlotsForm) {
      return;
    }
    const blockedSlot = _.find(
      blockedSlots,
      s => s.startAt.isSame(moment(event.start)) && s.endAt.isSame(event.end)
    );
    if (!blockedSlot) {
      return;
    }
    openMeeting.removeBlockedSlot(blockedSlot);
    updateEvents();
  };

  const handleBlockedSlotChange = (info: EventInfo) => {
    if (!showBlockedSlotsForm) {
      return;
    }
    const startAt = moment(info.start);
    const endAt = moment(info.end);
    if (startAt.isBefore(moment())) {
      showPastError();
      return;
    }

    const blockedSlot = _.find(
      blockedSlots,
      s => s.startAt.isSame(moment(info.event.start)) && s.endAt.isSame(info.event.end)
    );
    if (!blockedSlot) {
      return;
    }
    blockedSlot.startAt = startAt;
    blockedSlot.endAt = endAt;

    openMeetingBlockedSlotsChange(blockedSlots);
    updateEvents();
  };

  const handleSelectEvent = (event: Event, e: React.SyntheticEvent<HTMLElement, globalThis.Event>) => {
    if (selectedEvent) {
      if (selectedEvent.id && selectedEvent.id === event.id) {
        setSelectedEvent(null);
        return;
      }
      if (selectedEvent.name === event.name) {
        setSelectedEvent(null);
        return;
      }
    }
    if (event.isBlockedSlot) {
      setSelectedEvent(null);
      return;
    }
    setSelectedEvent(event);
    const click = e.nativeEvent as MouseEvent;
    setEventClickX(click.pageX - 550);
    setEventClickY(click.pageY - 100);
  };

  const getEventProp = (e: Event) => {
    const calendar = _.find(calendars, c => c.id === e.calendarId);
    const eventProps = {
      className: e.end.valueOf() - e.start.valueOf() < 1000 * 60 * 45 ? 'short_event' : '',
      style: {}
    };
    let backgroundColor = new Color(
      calendar && calendar.backgroundColor ? calendar.backgroundColor : muiTheme.palette.primary.main
    );
    if (e.end < moment()) {
      if (calendar && calendar.backgroundColor) {
        const lightness = backgroundColor.lightness();
        const newLightness = 100 - (100 - lightness) / 5;
        backgroundColor = backgroundColor.lightness(newLightness);
      } else {
        backgroundColor = backgroundColor.lighten(0.5);
      }
    }
    if (calendar && calendar.backgroundColor) {
      const color = new Color('#ffffff');
      eventProps.style = {
        backgroundColor: backgroundColor.hex(),
        color: calendar.foregroundColor || '#1d1d1d',
        borderColor: color.darken(0.0).hex()
      };
      return eventProps;
    }
    if (e.name === travelTimeTitle) {
      let color = new Color(muiTheme.palette.secondary.dark);
      if (e.end < moment()) color = color.lighten(0.3);
      eventProps.style = {
        backgroundColor: color.hex(),
        border: 0
      };
      return eventProps;
    }
    // 除外日時は破線ボーダー
    if (contextValue.isMobile) {
      eventProps.className += ' adjustment-candidate-event';
    }
    eventProps.style = {
      backgroundColor: 'rgba(191, 0, 0, 0.1)',
      color: muiTheme.palette.error.main,
      border: `dashed 2px ${muiTheme.palette.error.main}`
    };
    return eventProps;
  };

  const isEditable = (event: Event) => {
    return event.isBlockedSlot;
  };

  const handleRangeChange = (val: any) => {
    setSelectedCalendarView(val.length === 1 ? 'day' : 'week');
    setCalendarScrollTo(moment().set({ hour: 5, minute: 50 }).toDate());
  };

  const handleTouchStart = (e: any) => {
    setTouchStartedX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: any) => {
    setTouchMovedX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartedX !== 0 && touchMovedX !== 0) {
      const diff = selectedCalendarView === 'day' ? 1 : 7;
      if (touchStartedX - touchMovedX > 150) {
        onDateChange(date.add(diff, 'days'));
      }

      if (touchStartedX - touchMovedX < -150) {
        onDateChange(date.subtract(diff, 'days'));
      }
    }
    setTouchStartedX(0);
    setTouchMovedX(0);
  };

  const handleCalendarMarginTap = () => {
    if (!contextValue.isMobile || isLongTapInfoShown) {
      return;
    }

    contextValue.setSnackbarContent({
      message: 'カレンダーの空欄をロングタップで除外日時を追加',
      severity: 'info'
    });
    contextValue.setOpenSnackbar(true);
    setIsLongTapInfoShown(true);
  };

  return (
    <>
      <EventPopupCard
        event={selectedEvent}
        x={eventClickX}
        y={eventClickY}
        onClose={() => setSelectedEvent(null)}
      />
      <TheCalendarWrapper
        isMobile={contextValue.isMobile}
        onClick={() => handleCalendarMarginTap()}
        onTouchStart={e => handleTouchStart(e)}
        onTouchMove={e => handleTouchMove(e)}
        onTouchEnd={() => handleTouchEnd()}
      >
        {isEventLoading && <CalendarLoading />}
        <DragAndDropCalendar
          localizer={localizer}
          events={events}
          titleAccessor="name"
          startAccessor={event => event.start.toDate()}
          endAccessor={event => event.end.toDate()}
          defaultView="week"
          views={['day', 'week']}
          selectable={showBlockedSlotsForm}
          longPressThreshold={300}
          onRangeChange={val => handleRangeChange(val)}
          onSelectEvent={(event, e) => handleSelectEvent(event, e)}
          onDoubleClickEvent={event => handleDoubleClickEvent(event)}
          onSelectSlot={slot => handleSelectSlot(slot)}
          resizable={showBlockedSlotsForm}
          onEventResize={info => handleBlockedSlotChange(info)}
          onEventDrop={info => handleBlockedSlotChange(info)}
          draggableAccessor={info => isEditable(info)}
          eventPropGetter={e => getEventProp(e)}
          date={date.toDate()}
          onNavigate={date => {
            setSelectedEvent(null);
            onDateChange(moment(date));
          }}
          formats={formats}
          scrollToTime={calendarScrollTo}
          messages={messages}
        />
      </TheCalendarWrapper>
    </>
  );
};

const TheCalendarWrapper = styled(CalendarWrapper)<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    height: calc(100vh - 30px - 20px - 46px - 77px);
    padding-bottom: 77px;

    .rbc-toolbar {
      padding: 0 10px;
    }
  `}
`;

export default BigCalendar;
